import { useQuery } from '@tanstack/react-query';
import {
  CircleAlert,
  CircleCheck,
  ChevronDown,
  ChevronRight,
  Info,
  RefreshCw,
  ShieldCheck,
  TriangleAlert,
  Zap,
} from 'lucide-react';
import { Fragment, useState, type ReactNode } from 'react';
import { Link } from 'react-router';
import { isApiError } from '../api/client';
import { useApi } from '../api/context';
import { queryKeys, useNetwork, useNetworkActiveRuns } from '../api/queries';
import type {
  AppVariableChanges,
  ChangeAction,
  Deployment,
  PlanChange,
  ResourceType,
  Run,
  SecretMetadata,
  SecretsOverview,
  SharedChange,
  VariableChange,
} from '../api/types';
import { actionPresentation } from '../components/presentation';
import { StatusBadge } from '../components/ui';
import { formatRelative } from '../lib/format';
import {
  childrenSummary,
  dangerousChanges,
  vaultSecretsDeletionText,
  otherProperties,
  pulumiTypeLabel,
  reviewOrder,
  shortHash,
  type PlanBlocker,
} from '../lib/planReview';
import { TRIGGER_WAITING_FOR_CODE } from '../lib/triggers';
import { strings } from '../strings';
import { locationEntry, locationLabel } from '../variables/registry';
import {
  InterruptionCallouts,
  NetworkChips,
  NetworkPlanSection,
  RequestCallouts,
} from '../networks/runs/DeploymentPlanNetwork';
import { blockedRequests, closingRequests, spokeCounts } from '../networks/runs/runHelpers';
import { RunIssues } from './RunIssues';

const p = strings.planReview;
const ACTIONS: readonly ChangeAction[] = ['create', 'update', 'replace', 'delete', 'no-change'];
const GLYPH: Record<ChangeAction, string> = {
  create: '+',
  update: '~',
  replace: '±',
  delete: '−',
  'no-change': '=',
};
const CHANGE_GLYPH: Record<VariableChange['change'], string> = { added: '+', changed: '~', removed: '−' };
const CHANGE_TONE: Record<VariableChange['change'], string> = {
  added: 'success',
  changed: 'warning',
  removed: 'danger',
};

function sideText(side: VariableChange['before']): string {
  if (!side) return strings.common.dash;
  return side.value ?? side.expression ?? strings.common.dash;
}

function secretLine(change: VariableChange, secrets: readonly SecretMetadata[]): string {
  const side = change.after ?? change.before;
  const label = locationLabel({
    type: change.locationType,
    secret: true,
    label: change.locationType,
    container: null,
  });
  const reference = side?.secretName ? p.reference(label, side.secretName) : label;
  const metadata = secrets.find((s) => s.name.toLowerCase() === side?.secretName?.toLowerCase());
  const word = change.change === 'added' ? p.added : change.change === 'changed' ? p.changed : p.removedWord;
  const value =
    change.change === 'removed' || !metadata
      ? null
      : metadata.hasValue && metadata.updatedAt
        ? p.valueSet(formatRelative(metadata.updatedAt))
        : metadata.hasValue === false
          ? p.noValue
          : null;
  return [word, reference, value].filter(Boolean).join(' · ');
}

function VariableRows({ app, secrets }: { app: AppVariableChanges; secrets: readonly SecretMetadata[] }) {
  return (
    <ul className="plan-variables">
      {app.changes.map((change) => (
        <li key={change.name} data-testid={`plan-variable-${app.resourceId}/${change.name}`}>
          <span className={`glyph tone-${CHANGE_TONE[change.change]}`} aria-hidden>
            {CHANGE_GLYPH[change.change]}
          </span>
          <span className="sr-only">{p[change.change === 'removed' ? 'removedWord' : change.change]}</span>
          <span className="font-mono">{change.name}</span>
          <span className="min-w-0 text-fg-muted">
            {change.secret ? (
              secretLine(change, secrets)
            ) : change.change === 'added' ? (
              <span className="font-mono text-fg">{JSON.stringify(sideText(change.after))}</span>
            ) : change.change === 'removed' ? (
              <del className="font-mono">{JSON.stringify(sideText(change.before))}</del>
            ) : (
              <>
                <del className="font-mono">{JSON.stringify(sideText(change.before))}</del> →{' '}
                <span className="font-mono text-fg">{JSON.stringify(sideText(change.after))}</span>
              </>
            )}
            {' · '}
            {locationEntry({ type: change.locationType, secret: change.secret }).label({
              label: change.locationType,
              container: null,
            })}
          </span>
        </li>
      ))}
    </ul>
  );
}

export interface PlanReviewProps {
  run: Run;
  deployment: Deployment | undefined;
  latestVersion: number | undefined;
  blockers: PlanBlocker[];
  types: ReadonlyMap<string, ResourceType>;
  secrets: SecretsOverview | undefined;
  eventCount: number;
  onPlanAgain: () => void;
  onShowLog: () => void;
}

/** A completed (or failed) plan (spec 6.5 plan review, mock m08). */
export function PlanReview({
  run,
  deployment,
  latestVersion,
  blockers,
  types,
  secrets,
  eventCount,
  onPlanAgain,
  onShowLog,
}: PlanReviewProps) {
  const api = useApi();
  const plan = run.result?.plan ?? { changes: [], sharedChanges: [] };
  const completed = run.status === 'completed';
  const variableChanges = useQuery({
    queryKey: queryKeys.runVariableChanges(run.id),
    queryFn: () => api.getRunVariableChanges(run.id),
    enabled: completed,
    retry: false,
    staleTime: Infinity,
  });
  const notRecorded = !completed || isApiError(variableChanges.error, 'NOT_RECORDED');
  const apps = variableChanges.data?.apps ?? [];
  const appOf = (id: string) => apps.find((a) => a.resourceId === id);
  const [chips, setChips] = useState<ReadonlySet<ChangeAction>>(new Set());
  const [hideUnchanged, setHideUnchanged] = useState(true);
  const [open, setOpen] = useState<ReadonlySet<string>>(new Set());
  const [sharedOpen, setSharedOpen] = useState(false);
  const deploymentName = deployment?.name ?? '';
  // Feature spec 17 (6.15.16): the plan of an attached deployment (its settings snapshot names a network).
  const networkSetting = run.settings.network ?? null;
  const ownNetwork = deployment?.network?.id === networkSetting?.networkId ? deployment?.network : null;
  const networkQuery = useNetwork(networkSetting?.networkId ?? '', {
    enabled: !!networkSetting && !ownNetwork,
  });
  const networkName = ownNetwork?.name ?? networkQuery.data?.data.name ?? strings.networks.nav.kind;
  const networkQueue = useNetworkActiveRuns(networkSetting?.networkId ?? '');
  const queued = networkQueue.data?.items.length ?? 0;
  const [networkChips, setNetworkChips] = useState<ReadonlySet<ChangeAction>>(new Set());
  const spoke = plan.sharedChanges.filter((c) => c.role === 'spoke');
  const interruptions = run.result?.interruptions ?? [];
  const networkRequests = run.result?.networkRequests ?? [];
  const blocked = blockedRequests(networkRequests);
  const closing = closingRequests(networkRequests);
  const attachedWhere = ownNetwork?.attachments.length
    ? ownNetwork.attachments
    : interruptions.length
      ? interruptions
      : (run.result?.networkSteps ?? []).map((x) => {
          const [, , provider = '', region = ''] = x.componentKey.split('/');
          return { provider, region };
        });
  const deletion = run.result?.vaultSecretsDeletion ?? null;
  const dangers = dangerousChanges(plan, deploymentName, deployment?.vault?.name, deletion);
  const counts = Object.fromEntries(
    ACTIONS.map((a) => [a, plan.changes.filter((c) => c.action === a).length]),
  ) as Record<ChangeAction, number>;
  const rows = reviewOrder(plan.changes).filter((c) =>
    chips.size === 0 ? !(hideUnchanged && c.action === 'no-change') : chips.has(c.action),
  );
  const diagnostics = run.result?.diagnostics ?? [];
  const waiting = diagnostics.filter((d) => d.code === TRIGGER_WAITING_FOR_CODE).length;
  const restarting = apps.filter((a) => a.restarts);
  const changedVariables = apps.flatMap((a) => a.changes.map((c) => ({ app: a.resourceId, change: c })));
  const appliedVersion = deployment?.lastSuccessfulApply?.blueprintVersion ?? null;
  const secretMetadata = secrets?.secrets ?? [];
  const toggle = (id: string) =>
    setOpen((current) => {
      const next = new Set(current);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  const summaryOf = (change: PlanChange): ReactNode => {
    if (change.action === 'create') return p.newResource;
    if (change.action === 'delete') return p.removed;
    const app = appOf(change.resourceId);
    const parts = [
      ...(app && app.changes.length ? [p.variablesCount(app.changes.length)] : []),
      ...childrenSummary(change.children ?? []),
      ...otherProperties(change),
    ];
    if (parts.length === 0) return change.action === 'no-change' ? p.noChange : strings.common.dash;
    return (
      <>
        {parts.join(' · ')}
        {app?.restarts && (
          <>
            {' · '}
            <span className="font-semibold">{p.restarts}</span>
          </>
        )}
      </>
    );
  };
  const hasDetails = (change: PlanChange) =>
    !!types.get(change.type)?.injectionProperty ||
    (change.children ?? []).length > 0 ||
    otherProperties(change).length > 0;

  const shared = plan.sharedChanges.filter((c) => c.role !== 'spoke');
  const sharedChanged = [...shared.filter((c) => c.action !== 'no-change')].sort(
    (a, b) => Number(b.role === 'vault') - Number(a.role === 'vault'),
  );
  const sharedSame = shared.filter((c) => c.action === 'no-change');
  const label = (c: SharedChange) => pulumiTypeLabel(c.pulumiType);

  const vaultStatus = secrets?.vault.status;
  return (
    <div className="plan-review">
      <div className="plan-main">
        {completed &&
          deployment &&
          (blockers.filter((b) => b !== 'notCompleted').length === 0 ? (
            <p className="callout callout-info" data-testid="plan-freshness">
              <ShieldCheck aria-hidden className="size-4 shrink-0" />
              <span>
                <span className="font-semibold">{p.current}</span>{' '}
                {p.currentDetail(run.blueprintVersion ?? 0, shortHash(run.settingsHash))}
              </span>
            </p>
          ) : (
            <div className="callout callout-warning" data-testid="plan-freshness">
              <TriangleAlert aria-hidden className="size-4 shrink-0" />
              <span className="min-w-0 flex-1">
                <span className="font-semibold">{p.stale}</span>{' '}
                {blockers
                  .filter((b) => b !== 'notCompleted')
                  .map((b) =>
                    b === 'newerVersion'
                      ? p.staleReasons.newerVersion(latestVersion ?? 0)
                      : b === 'settingsChanged'
                        ? p.staleReasons.settingsChanged(
                            shortHash(run.settingsHash),
                            shortHash(deployment.settingsHash),
                          )
                        : p.staleReasons.appliedAfter,
                  )
                  .join('; ')}
                .
              </span>
              <button type="button" className="btn btn-sm" onClick={onPlanAgain}>
                {p.planAgain}
              </button>
            </div>
          ))}

        <RunIssues run={run} deployment={deployment} onPlanAgain={onPlanAgain} />
        {networkSetting && <InterruptionCallouts interruptions={interruptions} />}
        {networkSetting && (
          <RequestCallouts requests={networkRequests} networkId={networkSetting.networkId} />
        )}

        <div className="flex flex-wrap items-center gap-2" role="group" aria-label={p.chips}>
          {ACTIONS.map((action) => (
            <button
              key={action}
              type="button"
              className={`plan-chip tone-${actionPresentation[action].tone}`}
              aria-pressed={chips.has(action)}
              data-testid={`chip-${action}`}
              onClick={() =>
                setChips((current) => {
                  const next = new Set(current);
                  if (next.has(action)) next.delete(action);
                  else next.add(action);
                  return next;
                })
              }
            >
              <span className="glyph" aria-hidden>
                {GLYPH[action]}
              </span>
              {p.chip[action](counts[action])}
            </button>
          ))}
          {networkSetting && (
            <NetworkChips
              counts={run.networkChanges ?? spokeCounts(spoke)}
              selected={networkChips}
              onToggle={(action) =>
                setNetworkChips((current) => {
                  const next = new Set(current);
                  if (next.has(action)) next.delete(action);
                  else next.add(action);
                  return next;
                })
              }
            />
          )}
          <span className="flex-1" />
          <label className="switch">
            <input
              type="checkbox"
              role="switch"
              checked={hideUnchanged}
              onChange={(e) => setHideUnchanged(e.target.checked)}
            />
            {p.hideUnchanged}
          </label>
        </div>

        {dangers.length > 0 && (
          <div role="alert" className="callout callout-danger" data-testid="plan-dangers">
            <TriangleAlert aria-hidden className="size-4 shrink-0" />
            <div>
              <p className="font-semibold">{p.dangerTitle}</p>
              <ul className="mt-1 list-disc pl-5">
                {dangers.flatMap((danger) =>
                  danger.sentences.map((sentence) => <li key={`${danger.id}-${sentence}`}>{sentence}</li>),
                )}
              </ul>
              <p className="mt-1">{strings.impacts.typedNote(deploymentName)}</p>
            </div>
          </div>
        )}

        <section aria-labelledby="plan-changes-heading">
          <h2 id="plan-changes-heading" className="sr-only">
            {p.changes}
          </h2>
          {plan.changes.length === 0 ? (
            <p className="help">{p.noChanges}</p>
          ) : (
            <div className="overflow-x-auto rounded-lg border border-line bg-surface">
              <table className="table plan-table" aria-labelledby="plan-changes-heading">
                <thead>
                  <tr>
                    <th scope="col">{p.action}</th>
                    <th scope="col">{p.resource}</th>
                    <th scope="col">{p.type}</th>
                    <th scope="col">{p.summary}</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.length === 0 && (
                    <tr>
                      <td colSpan={4} className="help">
                        {p.noRows}
                      </td>
                    </tr>
                  )}
                  {rows.map((change) => {
                    const expanded = open.has(change.resourceId);
                    const details = hasDetails(change);
                    const app = appOf(change.resourceId);
                    const isApp = !!types.get(change.type)?.injectionProperty;
                    const danger =
                      !!change.stateful && (change.action === 'replace' || change.action === 'delete');
                    return (
                      <Fragment key={change.resourceId}>
                        <tr
                          data-testid={`plan-row-${change.resourceId}`}
                          className={danger ? 'is-danger' : undefined}
                        >
                          <td>
                            <StatusBadge presentation={actionPresentation[change.action]} />
                          </td>
                          <td>
                            {details ? (
                              <button
                                type="button"
                                className="plan-expand"
                                aria-expanded={expanded}
                                aria-label={p.expand(change.resourceId)}
                                onClick={() => toggle(change.resourceId)}
                              >
                                {expanded ? (
                                  <ChevronDown aria-hidden className="size-3.5" />
                                ) : (
                                  <ChevronRight aria-hidden className="size-3.5" />
                                )}
                                <span className="font-mono font-semibold">{change.resourceId}</span>
                              </button>
                            ) : (
                              <span className="font-mono font-semibold">{change.resourceId}</span>
                            )}
                          </td>
                          <td>{types.get(change.type)?.displayName ?? change.type}</td>
                          <td className="text-sm">{summaryOf(change)}</td>
                        </tr>
                        {expanded && (
                          <tr className="plan-details">
                            <td />
                            <td colSpan={3}>
                              {isApp && (
                                <div className="mb-2" data-testid={`plan-variables-${change.resourceId}`}>
                                  <h3 className="sr-only">{p.variablesHeading}</h3>
                                  {notRecorded ? (
                                    <p className="help">{p.notRecorded}</p>
                                  ) : variableChanges.isPending ? (
                                    <p className="help">{p.variablesLoading}</p>
                                  ) : app && app.changes.length > 0 ? (
                                    <>
                                      <VariableRows app={app} secrets={secretMetadata} />
                                      <p className="help mt-1">
                                        {p.variablesNote(appliedVersion)}{' '}
                                        {app.restarts && p.restartsNote(change.resourceId)} {p.noSecretValues}
                                      </p>
                                      {deployment && (
                                        <Link
                                          className="link text-sm"
                                          to={`/deployments/${deployment.id}?var=${encodeURIComponent(change.resourceId)}/${encodeURIComponent(app.changes[0].name)}`}
                                        >
                                          {p.openInVariables(change.resourceId)}
                                        </Link>
                                      )}
                                    </>
                                  ) : (
                                    <p className="help">{p.none}</p>
                                  )}
                                </div>
                              )}
                              {(change.children ?? []).length > 0 && (
                                <div className="mb-2">
                                  <h3 className="text-xs font-semibold text-fg-muted">{p.childrenHeading}</h3>
                                  <ul className="plan-variables">
                                    {(change.children ?? []).map((child) => (
                                      <li key={`${child.pulumiType}/${child.name}`} data-testid="plan-child">
                                        <span
                                          className={`glyph tone-${actionPresentation[child.action].tone}`}
                                          aria-hidden
                                        >
                                          {GLYPH[child.action]}
                                        </span>
                                        <span className="sr-only">
                                          {actionPresentation[child.action].label}
                                        </span>
                                        <span>{pulumiTypeLabel(child.pulumiType)}</span>
                                        <span className="font-mono text-fg-muted">{child.name}</span>
                                      </li>
                                    ))}
                                  </ul>
                                </div>
                              )}
                              {otherProperties(change).length > 0 && (
                                <div>
                                  <h3 className="text-xs font-semibold text-fg-muted">
                                    {p.propertiesHeading}
                                  </h3>
                                  <ul className="font-mono text-xs">
                                    {otherProperties(change).map((property) => (
                                      <li key={property}>{property}</li>
                                    ))}
                                  </ul>
                                </div>
                              )}
                            </td>
                          </tr>
                        )}
                      </Fragment>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </section>

        {networkSetting && (
          <NetworkPlanSection
            deployment={deploymentName}
            network={networkName}
            where={attachedWhere}
            spoke={spoke}
            selected={networkChips}
            steps={run.result?.networkSteps}
            requests={networkRequests}
          />
        )}

        <section aria-labelledby="plan-shared-heading">
          <h2 id="plan-shared-heading" className="mb-1 text-[15px] font-semibold">
            {p.shared} <span className="text-xs font-normal text-fg-muted">{p.sharedNote}</span>
          </h2>
          {deletion && (
            <p className="callout callout-danger mb-2" data-testid="plan-vault-deletion">
              {vaultSecretsDeletionText(deploymentName, deletion)}
            </p>
          )}
          {shared.length === 0 ? (
            deletion ? null : (
              <p className="help">{p.noShared}</p>
            )
          ) : (
            <div className="rounded-lg border border-line bg-surface p-3 text-sm" data-testid="plan-shared">
              {sharedChanged.length > 0 && (
                <ul className="mb-2 space-y-1">
                  {sharedChanged.map((c) => (
                    <li
                      key={`${c.provider}:${c.pulumiType}:${c.name}`}
                      className={`flex flex-wrap items-center gap-2 ${c.role === 'vault' && (c.action === 'replace' || c.action === 'delete') ? 'text-danger' : ''}`}
                    >
                      <StatusBadge presentation={actionPresentation[c.action]} />
                      <span>{label(c)}</span>
                      <span className="font-mono">{c.name}</span>
                      <span className="text-fg-muted">({c.provider})</span>
                    </li>
                  ))}
                </ul>
              )}
              {sharedSame.length > 0 && (
                <p>
                  <button
                    type="button"
                    className="plan-expand"
                    aria-expanded={sharedOpen}
                    onClick={() => setSharedOpen((v) => !v)}
                  >
                    <span className="glyph" aria-hidden>
                      =
                    </span>
                    {p.sharedUnchanged(
                      sharedSame.length,
                      sharedSame.map((c) => `${label(c)} ${c.name}`).join(', '),
                    )}
                  </button>
                </p>
              )}
              {sharedOpen && (
                <ul className="mt-1 space-y-1">
                  {sharedSame.map((c) => (
                    <li key={`${c.provider}:${c.pulumiType}:${c.name}`} className="flex flex-wrap gap-2">
                      <span>{label(c)}</span>
                      <span className="font-mono">{c.name}</span>
                      <span className="text-fg-muted">({c.provider})</span>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          )}
        </section>
      </div>

      <aside className="plan-side" aria-label={p.beforeYouApply}>
        <section className="side-card" aria-labelledby="before-apply-heading">
          <h2 id="before-apply-heading" className="side-card-title">
            {p.beforeYouApply}
          </h2>
          <ul className="side-checklist" data-testid="before-you-apply">
            <li>
              {vaultStatus === 'none' ? (
                <>
                  <Info aria-hidden className="size-4 text-fg-muted" />
                  {p.noSecrets}
                </>
              ) : vaultStatus === 'store' ? (
                <>
                  <Info aria-hidden className="size-4 text-fg-muted" />
                  {p.storeSecrets}
                </>
              ) : (secrets?.summary.missingValues ?? 0) > 0 ? (
                <>
                  <CircleAlert aria-hidden className="size-4 text-danger" />
                  {p.secretsMissing(secrets!.summary.missingValues)}
                </>
              ) : (
                <>
                  <CircleCheck aria-hidden className="size-4 text-success" />
                  {p.secretsOk}
                </>
              )}
            </li>
            <li>
              {dangers.length === 0 ? (
                <>
                  <CircleCheck aria-hidden className="size-4 text-success" />
                  {p.noDanger}
                </>
              ) : (
                <>
                  <TriangleAlert aria-hidden className="size-4 text-danger" />
                  {p.danger(dangers.length)}
                </>
              )}
            </li>
            <li data-testid="apps-restart">
              <RefreshCw aria-hidden className="size-4 text-warning" />
              {notRecorded ? (
                p.restartsNotRecorded
              ) : restarting.length === 0 ? (
                p.noRestarts
              ) : (
                <span>
                  {p.restartsList}{' '}
                  {restarting.map((a, i) => (
                    <Fragment key={a.resourceId}>
                      {i > 0 && ', '}
                      <span className="font-mono font-semibold">{a.resourceId}</span> {p.restartReason}
                    </Fragment>
                  ))}
                </span>
              )}
            </li>
            {networkSetting && interruptions.length > 0 && (
              <li data-testid="before-apply-interruption">
                <TriangleAlert aria-hidden className="size-4 text-warning" />
                {strings.networks.runs.beforeApplyInterrupted(queued)}
              </li>
            )}
            {networkSetting && blocked.length > 0 && (
              <li data-testid="before-apply-requests">
                <TriangleAlert aria-hidden className="size-4 text-warning" />
                {strings.networks.requests.pendingCallout(blocked.length, blocked[0].networkName)}
              </li>
            )}
            {networkSetting &&
              closing.map((text) => (
                <li key={text} data-testid="before-apply-closes">
                  <TriangleAlert aria-hidden className="size-4 text-warning" />
                  {text}
                </li>
              ))}
            {waiting > 0 && (
              <li>
                <Zap aria-hidden className="size-4 text-warning" />
                {p.triggersWaiting(waiting)}
              </li>
            )}
            <li>
              <Info aria-hidden className="size-4 text-fg-muted" />
              {p.failurePolicy(
                (deployment?.settings.onFailure ?? run.onFailure) === 'rollback'
                  ? strings.settings.rollback.toLowerCase()
                  : strings.settings.stop.toLowerCase(),
              )}
            </li>
          </ul>
        </section>

        <section className="side-card" aria-labelledby="plan-variables-heading">
          <h2 id="plan-variables-heading" className="side-card-title">
            {p.variablesPanel}
          </h2>
          {notRecorded ? (
            <p className="help">{p.notRecorded}</p>
          ) : changedVariables.length === 0 ? (
            <p className="help">{p.none}</p>
          ) : (
            <ul className="side-variables" data-testid="plan-variables-panel">
              {changedVariables.map(({ app, change }) => {
                const Icon = locationEntry({ type: change.locationType, secret: change.secret }).icon;
                const text = change.secret
                  ? change.change === 'removed'
                    ? p.removedValue
                    : secretMetadata.find((x) => x.name === (change.after?.secretName ?? ''))?.hasValue
                      ? p.newValueSet
                      : p.newNoValue
                  : change.change === 'added'
                    ? p.newValue(sideText(change.after))
                    : change.change === 'removed'
                      ? p.removedValue
                      : `${sideText(change.before)} → ${sideText(change.after)}`;
                return (
                  <li key={`${app}/${change.name}`}>
                    {deployment ? (
                      <Link
                        className="font-mono font-semibold"
                        to={`/deployments/${deployment.id}?var=${encodeURIComponent(app)}/${encodeURIComponent(change.name)}`}
                      >
                        {change.name}
                      </Link>
                    ) : (
                      <span className="font-mono font-semibold">{change.name}</span>
                    )}
                    <Icon aria-hidden className="size-3.5 text-fg-muted" />
                    <span className="text-fg-muted">{text}</span>
                  </li>
                );
              })}
            </ul>
          )}
        </section>

        <section className="side-card" aria-labelledby="plan-run-heading">
          <h2 id="plan-run-heading" className="side-card-title">
            {p.runPanel}
          </h2>
          <dl className="side-facts">
            <dt>{p.kind}</dt>
            <dd>
              {strings.status.runKind[run.kind].toLowerCase()} · {run.scope ?? 'full'}
            </dd>
            <dt>{p.triggeredBy}</dt>
            <dd>{run.createdBy}</dd>
            <dt>{p.version}</dt>
            <dd>v{run.blueprintVersion ?? '?'}</dd>
            <dt>{p.settings}</dt>
            <dd>
              {p.snapshot} · {p.hash(shortHash(run.settingsHash))}
            </dd>
            {networkSetting && (
              <>
                <dt>{strings.networks.runs.network}</dt>
                <dd>
                  <Link className="link" to={`/networks/${networkSetting.networkId}`}>
                    {networkName}
                  </Link>
                </dd>
              </>
            )}
            <dt>{p.log}</dt>
            <dd>
              <button type="button" className="link" onClick={onShowLog}>
                {p.events(eventCount)}
              </button>
            </dd>
          </dl>
        </section>
      </aside>
    </div>
  );
}
